import i18n from '@/i18n';

/**
 * Translate a structured server message into the active locale.
 *
 * Convention: backend endpoints that return user-facing states/errors send
 *   { code: 'template_token_missing', params: { token: 'rating' }, message: 'Token rating ...' }
 * The code maps to an i18n key under `server.<code>` (see src/locales/*.json),
 * with `params` used for named interpolation. When the code is missing or has
 * no translation (older backend, newly added codes), the English `message`
 * text is shown as-is — the UI never breaks on an unknown code.
 *
 * List-valued params (e.g. {missing: ['affiliate_link', 'image']}) are
 * translated item-by-item via `server.fields.<item>` and joined, so the
 * backend can send identifiers instead of English words.
 *
 * @param {{code?: string, params?: Object, message?: string}|null} info
 * @param {string} [fallback] shown when info is empty and has no message
 * @returns {string}
 */
export function translateServerMessage(info, fallback = '') {
  if (!info) return fallback;

  const { code, params, message } = info;
  if (code) {
    const key = `server.${code}`;
    // te() checks the active locale only, so also probe the fallback locale
    // before trusting the key — t() on a fully-unknown key would render the
    // raw key string instead of a message.
    if (i18n.global.te(key) || i18n.global.te(key, 'en')) {
      return i18n.global.t(key, resolveParams(params));
    }
  }
  return message || fallback;
}

/**
 * Translate an error thrown by the API helpers (see wrapApiError in
 * src/utils/apiError.js): prefers the structured `error.detail` payload,
 * falls back to the English `error.message` / the given fallback.
 *
 * @param {{detail?: Object, message?: string}|Error|null} error
 * @param {string} [fallback]
 * @returns {string}
 */
export function translateApiError(error, fallback = '') {
  if (!error) return fallback;
  return translateServerMessage(error.detail, error.message || fallback);
}

/**
 * Translate a single server field identifier (e.g. 'affiliate_link') via the
 * `server.fields.<field>` i18n keys; falls back to a humanized identifier.
 */
export function translateServerField(field) {
  const key = `server.fields.${field}`;
  if (i18n.global.te(key) || i18n.global.te(key, 'en')) {
    return i18n.global.t(key);
  }
  return String(field).replace(/_/g, ' ');
}

function resolveParams(params) {
  if (!params) return {};
  const resolved = {};
  for (const [key, value] of Object.entries(params)) {
    resolved[key] = Array.isArray(value)
      ? value.map(translateServerField).join(', ')
      : value;
  }
  return resolved;
}
